import React, { useState, useEffect, useCallback } from "react";
import { Container, Alert, Modal, Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import SettingsPageHeader from "../components/SettingsPageHeader";
import { useAuth } from "../hooks/useAuth";
import { useRolePermissions } from "../hooks/useRolePermissions";
import {
  companyGroupAPI,
  companyLocationAPI,
  companyAPI,
  clearCache,
  createCompanyGroup,
  createCompanyLocation,
} from "../utils/api";
import type {
  CompanyGroup,
  CompanyLocation,
  InheritableSettings,
} from "../types/companyGroup";
import type {
  CompanyGroupCreationData,
  CompanyLocationCreationData,
} from "../utils/api";
import type { Company } from "../types/auth";
import HierarchyTree from "./HierarchyTree";
import WizardModal from "./WizardModal";
import { useWizardState } from "./HierarchyManagementHooks";
import {
  sanitizeInheritedValue,
  getTimezoneValue,
  isDirectOverride,
} from "./HierarchyManagementUtils";
import { SETTINGS_KEYS, DEFAULT_SETTINGS_VALUES } from "./HierarchyManagementConstants";

type WizardData = Partial<
  CompanyGroupCreationData &
    CompanyLocationCreationData & {
      company_id: string;
      company_name: string;
      status: 1 | 2;
      email?: string;
      phone?: string;
      description?: string;
      company_group_id?: number;
      location_name?: string;
      location_description?: string;
      location_timezone?: string;
      location_address?: string;
      location_repo_th?: number;
      location_similarity_addition?: number;
      location_valid_th_from?: number;
      location_valid_th_to?: number;
      location_ppe_types?: string[];
      location_ppe_min_confidence?: number;
      location_shifts_enabled?: boolean;
      location_payroll_enabled?: boolean;
      location_shift_auto_detect?: boolean;
      company_timezone?: string;
      company_address?: string;
      company_repo_th?: number;
      company_similarity_addition?: number;
      company_valid_th_from?: number;
      company_valid_th_to?: number;
      company_ppe_types?: string[];
      company_ppe_min_confidence?: number;
      company_shifts_enabled?: boolean;
      company_payroll_enabled?: boolean;
      company_shift_auto_detect?: boolean;
    } & InheritableSettings
>;

export type { WizardData };

const HierarchyManagement: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'hierarchyManagement' });
  const { currentCompany, userRolePermissionsLoading } = useAuth();
  const { isSuperAdmin } = useRolePermissions();

  const [groups, setGroups] = useState<CompanyGroup[]>([]);
  const [locations, setLocations] = useState<CompanyLocation[]>([]);
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalType, setModalType] = useState<
    "group" | "location" | "company" | null
  >(null);
  const [modalAction, setModalAction] = useState<
    "create" | "edit" | "assign-company"
  >("create");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSuccess, setModalSuccess] = useState<string | null>(null);

  const {
    wizardStep,
    setWizardStep,
    wizardData,
    setWizardData,
    companyMode,
    setCompanyMode,
    override,
    setOverride,
    initialOverride,
    setInitialOverride,
    companyOverride,
    setCompanyOverride,
    initialCompanyOverride,
    setInitialCompanyOverride,
    resetWizard,
  } = useWizardState();

  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => Promise<void>;
  } | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const closeConfirmDialog = () => {
    setConfirmLoading(false);
    setConfirmDialog(null);
  };

  const openConfirmDialog = (
    title: string,
    message: string,
    onConfirm: () => Promise<void>,
  ) => {
    setConfirmDialog({ show: true, title, message, onConfirm });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalError(null);
    setModalSuccess(null);
    setActionLoading(false);
    resetWizard();
    setEditingId(null);
  };

  const fetchData = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);

        const groupsRes = await companyGroupAPI.listGroups();
        setGroups(groupsRes.data);

        const locationsPromises = groupsRes.data.map((group) =>
          companyLocationAPI.listLocations(group.id),
        );
        const locationsRes = await Promise.all(locationsPromises);
        setLocations(locationsRes.flatMap((res) => res.data));

        const companiesRes = await companyAPI.getCompanies();
        setAllCompanies(companiesRes.data);
      } catch {
        setError(t('fetchFailed'));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    clearCache();
    fetchData();
  }, [fetchData]);

  const getLocationsForGroup = (groupId: number) =>
    locations.filter((l) => l.company_group_id === groupId);

  const getCompaniesForLocation = (locationId: number) =>
    allCompanies.filter((c) => c.company_location_id === locationId);

  const handleOpenCreateModal = (
    type: "group" | "location",
    parentId?: number,
  ) => {
    setModalType(type);
    setModalAction("create");
    setEditingId(null);
    setModalError(null);
    setModalSuccess(null);
    setWizardStep(1);

    const initialData: WizardData = {
      ...DEFAULT_SETTINGS_VALUES,
    };

    if (type === "location" && parentId) {
      initialData.company_group_id = parentId;
      const group = groups.find((g) => g.id === parentId);
      if (group) {
        initialData.timezone = group.timezone ?? getTimezoneValue(group.timezone);
        initialData.address = group.address ?? "";
        initialData.repo_th = group.repo_th ?? DEFAULT_SETTINGS_VALUES.repo_th;
        initialData.similarity_addition = group.similarity_addition ?? DEFAULT_SETTINGS_VALUES.similarity_addition;
        initialData.valid_th_from = group.valid_th_from ?? DEFAULT_SETTINGS_VALUES.valid_th_from;
        initialData.valid_th_to = group.valid_th_to ?? DEFAULT_SETTINGS_VALUES.valid_th_to;
        initialData.ppe_types = group.ppe_types ?? DEFAULT_SETTINGS_VALUES.ppe_types;
        initialData.ppe_min_confidence = group.ppe_min_confidence ?? DEFAULT_SETTINGS_VALUES.ppe_min_confidence;
        initialData.shifts_enabled = group.shifts_enabled ?? DEFAULT_SETTINGS_VALUES.shifts_enabled;
        initialData.shift_auto_detect = group.shift_auto_detect ?? DEFAULT_SETTINGS_VALUES.shift_auto_detect;
      }
    }
    setWizardData(initialData);
    setCompanyMode("attach");
    if (type === "location") {
      const initialOver = SETTINGS_KEYS.reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as Record<string, boolean>);
      setOverride(initialOver);
      setInitialOverride(initialOver);
    }
    setShowModal(true);
  };

  const handleOpenEditModal = (type: "group" | "location", item: any) => {
    setModalType(type);
    setModalAction("edit");
    setEditingId(item.id);
    setModalError(null);
    setModalSuccess(null);
    setWizardStep(1);
    setCompanyMode("attach");

    if (type === "group") {
      setWizardData({
        name: item.name,
        description: item.description || "",
        timezone: sanitizeInheritedValue(item.timezone) || "",
        address: sanitizeInheritedValue(item.address) || "",
        repo_th: sanitizeInheritedValue(item.repo_th) ?? 0,
        similarity_addition: sanitizeInheritedValue(item.similarity_addition) ?? 0,
        valid_th_from: sanitizeInheritedValue(item.valid_th_from) ?? 0,
        valid_th_to: sanitizeInheritedValue(item.valid_th_to) ?? 0,
        ppe_types: sanitizeInheritedValue(item.ppe_types) ?? [],
        ppe_min_confidence: sanitizeInheritedValue(item.ppe_min_confidence) ?? 0.8,
        shifts_enabled: sanitizeInheritedValue(item.shifts_enabled) ?? undefined,
        payroll_enabled: sanitizeInheritedValue(item.payroll_enabled) ?? undefined,
        shift_auto_detect: sanitizeInheritedValue(item.shift_auto_detect) ?? undefined,
      });
    } else {
      const group = groups.find((g) => g.id === item.company_group_id);
      const groupValues = {
        timezone: sanitizeInheritedValue(group?.timezone),
        address: sanitizeInheritedValue(group?.address),
        repo_th: sanitizeInheritedValue(group?.repo_th),
        similarity_addition: sanitizeInheritedValue(group?.similarity_addition),
        valid_th_from: sanitizeInheritedValue(group?.valid_th_from),
        valid_th_to: sanitizeInheritedValue(group?.valid_th_to),
        ppe_types: sanitizeInheritedValue(group?.ppe_types),
        ppe_min_confidence: sanitizeInheritedValue(group?.ppe_min_confidence),
        shifts_enabled: sanitizeInheritedValue(group?.shifts_enabled),
        payroll_enabled: sanitizeInheritedValue(group?.payroll_enabled),
        shift_auto_detect: sanitizeInheritedValue(group?.shift_auto_detect),
      };
      const locValues = {
        timezone: sanitizeInheritedValue(item.timezone),
        address: sanitizeInheritedValue(item.address),
        repo_th: sanitizeInheritedValue(item.repo_th),
        similarity_addition: sanitizeInheritedValue(item.similarity_addition),
        valid_th_from: sanitizeInheritedValue(item.valid_th_from),
        valid_th_to: sanitizeInheritedValue(item.valid_th_to),
        ppe_types: sanitizeInheritedValue(item.ppe_types),
        ppe_min_confidence: sanitizeInheritedValue(item.ppe_min_confidence),
        shifts_enabled: sanitizeInheritedValue(item.shifts_enabled),
        payroll_enabled: sanitizeInheritedValue(item.payroll_enabled),
        shift_auto_detect: sanitizeInheritedValue(item.shift_auto_detect),
      };
      setWizardData({
        company_group_id: item.company_group_id,
        timezone: groupValues.timezone || undefined,
        address: groupValues.address || undefined,
        repo_th: groupValues.repo_th ?? undefined,
        similarity_addition: groupValues.similarity_addition ?? undefined,
        valid_th_from: groupValues.valid_th_from ?? undefined,
        valid_th_to: groupValues.valid_th_to ?? undefined,
        ppe_types: groupValues.ppe_types || undefined,
        ppe_min_confidence: groupValues.ppe_min_confidence ?? undefined,
        shifts_enabled: groupValues.shifts_enabled ?? undefined,
        payroll_enabled: groupValues.payroll_enabled ?? undefined,
        shift_auto_detect: groupValues.shift_auto_detect ?? undefined,

        location_name: item.name,
        location_description: item.description || "",
        location_timezone: locValues.timezone || "",
        location_address: locValues.address || "",
        location_repo_th: locValues.repo_th ?? undefined,
        location_similarity_addition: locValues.similarity_addition ?? undefined,
        location_valid_th_from: locValues.valid_th_from ?? undefined,
        location_valid_th_to: locValues.valid_th_to ?? undefined,
        location_ppe_types: locValues.ppe_types || undefined,
        location_ppe_min_confidence: locValues.ppe_min_confidence ?? undefined,
        location_shifts_enabled: locValues.shifts_enabled ?? undefined,
        location_payroll_enabled: locValues.payroll_enabled ?? undefined,
        location_shift_auto_detect: locValues.shift_auto_detect ?? undefined,
      });
      const currentOverrides = SETTINGS_KEYS.reduce((acc, key) => {
        acc[key] = isDirectOverride(locValues[key], groupValues[key], key);
        return acc;
      }, {} as Record<string, boolean>);
      setOverride(currentOverrides);
      setInitialOverride(currentOverrides);
    }
    setShowModal(true);
  };

  const handleOpenEditCompanyModal = (comp: Company) => {
    setModalType("company");
    setModalAction("edit");
    setEditingId(comp.id);
    setModalError(null);
    setModalSuccess(null);
    setWizardStep(1);
    setCompanyMode("attach");

    const loc = locations.find((l) => l.id === comp.company_location_id);
    const group = groups.find((g) => g.id === loc?.company_group_id);

    const groupValues = {
      timezone: sanitizeInheritedValue(group?.timezone),
      address: sanitizeInheritedValue(group?.address),
      repo_th: sanitizeInheritedValue(group?.repo_th),
      similarity_addition: sanitizeInheritedValue(group?.similarity_addition),
      valid_th_from: sanitizeInheritedValue(group?.valid_th_from),
      valid_th_to: sanitizeInheritedValue(group?.valid_th_to),
      ppe_types: sanitizeInheritedValue(group?.ppe_types),
      ppe_min_confidence: sanitizeInheritedValue(group?.ppe_min_confidence),
      shifts_enabled: sanitizeInheritedValue(group?.shifts_enabled),
      payroll_enabled: sanitizeInheritedValue(group?.payroll_enabled),
      shift_auto_detect: sanitizeInheritedValue(group?.shift_auto_detect),
    };
    const locationValues = {
      timezone: sanitizeInheritedValue(loc?.timezone),
      address: sanitizeInheritedValue(loc?.address),
      repo_th: sanitizeInheritedValue(loc?.repo_th),
      similarity_addition: sanitizeInheritedValue(loc?.similarity_addition),
      valid_th_from: sanitizeInheritedValue(loc?.valid_th_from),
      valid_th_to: sanitizeInheritedValue(loc?.valid_th_to),
      ppe_types: sanitizeInheritedValue(loc?.ppe_types),
      ppe_min_confidence: sanitizeInheritedValue(loc?.ppe_min_confidence),
      shifts_enabled: sanitizeInheritedValue(loc?.shifts_enabled),
      payroll_enabled: sanitizeInheritedValue(loc?.payroll_enabled),
      shift_auto_detect: sanitizeInheritedValue(loc?.shift_auto_detect),
    };
    const effectiveLocationValues = {
      timezone: locationValues.timezone ?? groupValues.timezone,
      address: locationValues.address ?? groupValues.address,
      repo_th: locationValues.repo_th ?? groupValues.repo_th,
      similarity_addition: locationValues.similarity_addition ?? groupValues.similarity_addition,
      valid_th_from: locationValues.valid_th_from ?? groupValues.valid_th_from,
      valid_th_to: locationValues.valid_th_to ?? groupValues.valid_th_to,
      ppe_types: locationValues.ppe_types ?? groupValues.ppe_types,
      ppe_min_confidence: locationValues.ppe_min_confidence ?? groupValues.ppe_min_confidence,
      shifts_enabled: locationValues.shifts_enabled ?? groupValues.shifts_enabled,
      payroll_enabled: locationValues.payroll_enabled ?? groupValues.payroll_enabled,
      shift_auto_detect: locationValues.shift_auto_detect ?? groupValues.shift_auto_detect,
    };

    const companyValues = {
      timezone: sanitizeInheritedValue(comp.timezone),
      address: sanitizeInheritedValue(comp.address),
      repo_th: sanitizeInheritedValue(comp.repo_th),
      similarity_addition: sanitizeInheritedValue(comp.similarity_addition),
      valid_th_from: sanitizeInheritedValue(comp.valid_th_from),
      valid_th_to: sanitizeInheritedValue(comp.valid_th_to),
      ppe_types: sanitizeInheritedValue(comp.ppe_types),
      ppe_min_confidence: sanitizeInheritedValue(comp.ppe_min_confidence),
      shifts_enabled: sanitizeInheritedValue(comp.shifts_enabled),
      payroll_enabled: sanitizeInheritedValue(comp.payroll_enabled),
      shift_auto_detect: sanitizeInheritedValue(comp.shift_auto_detect),
    };

    setWizardData({
      company_id: comp.company_id,
      company_name: comp.name,
      status: comp.status as 1 | 2,
      email: comp.email || "",
      phone: comp.phone || "",
      description: comp.description || "",
      location_timezone: effectiveLocationValues.timezone || "",
      location_address: effectiveLocationValues.address || "",
      location_repo_th: effectiveLocationValues.repo_th ?? undefined,
      location_similarity_addition: effectiveLocationValues.similarity_addition ?? undefined,
      location_valid_th_from: effectiveLocationValues.valid_th_from ?? undefined,
      location_valid_th_to: effectiveLocationValues.valid_th_to ?? undefined,
      location_ppe_types: effectiveLocationValues.ppe_types || undefined,
      location_ppe_min_confidence: effectiveLocationValues.ppe_min_confidence ?? undefined,
      location_shifts_enabled: effectiveLocationValues.shifts_enabled ?? undefined,
      location_payroll_enabled: effectiveLocationValues.payroll_enabled ?? undefined,
      location_shift_auto_detect: effectiveLocationValues.shift_auto_detect ?? undefined,

      company_timezone: companyValues.timezone || "",
      company_address: companyValues.address || "",
      company_repo_th: companyValues.repo_th ?? undefined,
      company_similarity_addition: companyValues.similarity_addition ?? undefined,
      company_valid_th_from: companyValues.valid_th_from ?? undefined,
      company_valid_th_to: companyValues.valid_th_to ?? undefined,
      company_ppe_types: companyValues.ppe_types || undefined,
      company_ppe_min_confidence: companyValues.ppe_min_confidence ?? undefined,
      company_shifts_enabled: companyValues.shifts_enabled ?? undefined,
      company_payroll_enabled: companyValues.payroll_enabled ?? undefined,
      company_shift_auto_detect: companyValues.shift_auto_detect ?? undefined,
    });

    const newOverrides: Record<string, boolean> = {};
    SETTINGS_KEYS.forEach((s) => {
      const compVal = companyValues[s];
      const parentVal = locationValues[s] ?? groupValues[s];
      newOverrides[s] = isDirectOverride(compVal, parentVal, s);
    });
    setCompanyOverride(newOverrides);
    setInitialCompanyOverride(newOverrides);
    setShowModal(true);
  };

  const handleOpenAssignModal = (loc: CompanyLocation) => {
    setModalType("location");
    setModalAction("assign-company");
    setEditingId(loc.id);
    setModalError(null);
    setModalSuccess(null);
    setWizardStep(1);
    setCompanyMode("attach");

    const group = groups.find((g) => g.id === loc.company_group_id);
    setWizardData({
      company_group_id: loc.company_group_id,
      timezone: group?.timezone || undefined,
      address: group?.address || undefined,
      repo_th: group?.repo_th || undefined,
      similarity_addition: group?.similarity_addition || undefined,
      valid_th_from: group?.valid_th_from || undefined,
      valid_th_to: group?.valid_th_to || undefined,
      ppe_types: group?.ppe_types || undefined,
      ppe_min_confidence: group?.ppe_min_confidence || undefined,
      shifts_enabled: group?.shifts_enabled ?? undefined,
      shift_auto_detect: group?.shift_auto_detect ?? undefined,

      // Actual values at location level
      location_timezone: loc.timezone || undefined,
      location_address: loc.address || undefined,
      location_repo_th: loc.repo_th ?? undefined,
      location_similarity_addition: loc.similarity_addition ?? undefined,
      location_valid_th_from: loc.valid_th_from ?? undefined,
      location_valid_th_to: loc.valid_th_to ?? undefined,
      location_ppe_types: loc.ppe_types || undefined,
      location_ppe_min_confidence: loc.ppe_min_confidence ?? undefined,
      location_shifts_enabled: loc.shifts_enabled ?? undefined,
      location_shift_auto_detect: loc.shift_auto_detect ?? undefined,
    });
    setCompanyOverride({});
    setShowModal(true);
  };

  const handleUnassignCompany = async (comp: Company) => {
    if (!comp.company_location_id) return;

    const companiesInLocation = getCompaniesForLocation(
      comp.company_location_id,
    );
    if (companiesInLocation.length <= 1) {
      setError(t('lastCompanyUnassign'));
      return;
    }

    openConfirmDialog(
      t('confirmUnassignTitle'),
      t('confirmUnassignMessage', { name: comp.name }),
      async () => {
        setConfirmLoading(true);
        try {
          const { id } = comp;
          await companyAPI.unassignLocation(id);
          fetchData(true);
          closeConfirmDialog();
        } catch (err: any) {
          setError(err.response?.data?.message || t('unassignFailed'));
          setConfirmLoading(false);
        }
      },
    );
  };

  const handleDelete = async (
    type: "group" | "location",
    id: number,
    parentId?: number,
  ) => {
    if (type === "location") {
      const locationsInGroup = getLocationsForGroup(parentId!);
      if (locationsInGroup.length <= 1) {
        setError(t('lastLocationDelete'));
        return;
      }
    }

    const entityName = type === "group" ? t('entityGroup') : t('entityLocation');
    openConfirmDialog(
      t('deleteEntityTitle', { entity: entityName }),
      t('deleteEntityMessage', { entity: entityName }),
      async () => {
        setConfirmLoading(true);
        try {
          if (type === "group") {
            await companyGroupAPI.deleteGroup(id);
          } else {
            await companyLocationAPI.deleteLocation(parentId!, id);
          }
          fetchData(true);
          closeConfirmDialog();
        } catch (err: any) {
          setError(err.response?.data?.message || t('deleteFailed'));
          setConfirmLoading(false);
        }
      },
    );
  };

  const handleNext = () => {
    setModalError(null);
    if (modalType === "group") {
      if (wizardStep === 1 && !wizardData.name?.trim()) {
        setModalError(t('groupNameRequired'));
        return;
      }
      if (wizardStep === 2 && !wizardData.location_name?.trim()) {
        setModalError(t('locationNameRequired'));
        return;
      }
    } else if (modalType === "location") {
      if (wizardStep === 1 && !wizardData.location_name?.trim()) {
        setModalError(t('locationNameRequired'));
        return;
      }
    }
    setWizardStep((s) => s + 1);
  };

  const handleBack = () => {
    setModalError(null);
    setWizardStep((s) => s - 1);
  };

  const helperSendCompanySettings = (
    prefix: "company" | "location" = "company",
  ) => {
    const payload: any = {};
    SETTINGS_KEYS.forEach((s) => {
      const isOverridden =
        prefix === "company" ? companyOverride[s] : override[s];
      const initialIsOverridden =
        prefix === "company" ? initialCompanyOverride[s] : initialOverride[s];
      const valueKey = prefix === "company" ? `company_${s}` : `location_${s}`;
      const shiftsValueKey = prefix === "company" ? "company_shifts_enabled" : "location_shifts_enabled";
      const inheritedShiftsEnabled = prefix === "company"
        ? (wizardData.location_shifts_enabled ?? wizardData.shifts_enabled)
        : wizardData.shifts_enabled;
      const shiftsEnabled = wizardData[shiftsValueKey as keyof WizardData] ?? inheritedShiftsEnabled;

      if (s === "shift_auto_detect" && !shiftsEnabled) {
        if (isOverridden || initialIsOverridden) payload[s] = false;
        return;
      }

      if (isOverridden) {
        let val = wizardData[valueKey as keyof WizardData];
        val = sanitizeInheritedValue(val);

        if (s === "timezone") {
          val = getTimezoneValue(val as string);
        }

        if (val !== undefined && val !== null) {
          payload[s] = val;
        } else if (
          [
            "repo_th",
            "similarity_addition",
            "valid_th_from",
            "valid_th_to",
            "ppe_min_confidence",
          ].includes(s)
        ) {
          payload[s] = 0;
        } else if (s === "ppe_types") {
          payload[s] = [];
        } else if (s === "shifts_enabled" || s === "shift_auto_detect") {
          payload[s] = false;
        } else {
          payload[s] = null;
        }
      } else if (initialIsOverridden) {
        payload[s] = null;
      }
    });
    return payload;
  };

  const handleWizardSubmit = async () => {
    setModalError(null);

    if (
      modalAction === "assign-company" ||
      (modalAction === "create" &&
        (modalType === "group" ? wizardStep === 3 : wizardStep === 2))
    ) {
      if (companyMode === "attach" && !wizardData.company_id?.trim()) {
        setModalError(t('companyRequired'));
        return;
      }
      if (companyMode === "create") {
        if (!wizardData.company_id?.trim()) {
          setModalError(t('companyIdRequired'));
          return;
        }
        if (!wizardData.company_name?.trim()) {
          setModalError(t('companyNameRequired'));
          return;
        }
      }
    }

    setActionLoading(true);
    try {
      const companySettings = helperSendCompanySettings("company");

      if (modalAction === "assign-company") {
        if (companyMode === "attach") {
          const comp = allCompanies.find(
            (c) => c.company_id === wizardData.company_id,
          );
          if (comp) {
            const { id, ...fullData } = comp;
            await companyAPI.updateCompany(id, {
              ...companySettings,
              email: fullData.email ?? undefined,
              company_location_id: editingId,
              name: fullData.name,
              phone: fullData.phone,
              description: fullData.description,
              status: fullData.status,
            });
          }
        } else {
          await companyAPI.createCompany({
            company_id: wizardData.company_id!,
            name: wizardData.company_name!,
            status: wizardData.status || 1,
            company_location_id: editingId,
            timezone: getTimezoneValue(wizardData.location_timezone),
            email: wizardData.email,
            phone: wizardData.phone,
            description: wizardData.description,
            ...companySettings,
          });
        }
      } else if (modalType === "company" && modalAction === "edit") {
        const comp = allCompanies.find((c) => c.id === editingId);
        if (comp) {
          const { id, ...fullData } = comp;
          await companyAPI.updateCompany(id, {
            ...fullData,
            name: wizardData.company_name,
            status: wizardData.status,
            email: wizardData.email,
            phone: wizardData.phone,
            description: wizardData.description,
            ...companySettings,
          });
        }
      } else if (modalType === "group") {
        if (modalAction === "create") {
          const locationSettings: any = {};
          const locationShiftsEnabled = override.shifts_enabled
            ? wizardData.location_shifts_enabled
            : wizardData.shifts_enabled;
          SETTINGS_KEYS.forEach((s) => {
            if (override[s]) {
              const value = wizardData[`location_${s}` as keyof WizardData];
              if (s === "shift_auto_detect" && !locationShiftsEnabled) return;
              locationSettings[s] = s === "shifts_enabled" || s === "shift_auto_detect"
                ? value ?? false
                : value;
            }
          });

          const payload: CompanyGroupCreationData = {
            name: wizardData.name!,
            description: wizardData.description,
            timezone: getTimezoneValue(wizardData.timezone),
            address: wizardData.address,
            repo_th: wizardData.repo_th,
            similarity_addition: wizardData.similarity_addition,
            valid_th_from: wizardData.valid_th_from,
            valid_th_to: wizardData.valid_th_to,
            ppe_types: wizardData.ppe_types,
            ppe_min_confidence: wizardData.ppe_min_confidence,
            shifts_enabled: wizardData.shifts_enabled ?? false,
            payroll_enabled: wizardData.payroll_enabled ?? false,
            shift_auto_detect: wizardData.shifts_enabled ? wizardData.shift_auto_detect ?? false : false,
            location: {
              name: wizardData.location_name!,
              description: wizardData.location_description,
              ...locationSettings,
            },
            company: {
              mode: companyMode,
              company_id: wizardData.company_id!,
              name:
                companyMode === "create" ? wizardData.company_name : undefined,
              status:
                companyMode === "create" ? wizardData.status || 1 : undefined,
              email: companyMode === "create" ? wizardData.email : undefined,
              phone: companyMode === "create" ? wizardData.phone : undefined,
              description: companyMode === "create" ? wizardData.description : undefined,
              ...companySettings,
            },
          };
          await createCompanyGroup(payload);
        } else {
          await companyGroupAPI.updateGroup(editingId!, {
            name: wizardData.name,
            description: wizardData.description,
            timezone: getTimezoneValue(wizardData.timezone),
            address: wizardData.address,
            repo_th: wizardData.repo_th,
            similarity_addition: wizardData.similarity_addition,
            valid_th_from: wizardData.valid_th_from,
            valid_th_to: wizardData.valid_th_to,
            ppe_types: wizardData.ppe_types,
            ppe_min_confidence: wizardData.ppe_min_confidence,
            shifts_enabled: wizardData.shifts_enabled ?? null,
            payroll_enabled: wizardData.payroll_enabled ?? null,
            shift_auto_detect: wizardData.shifts_enabled === true
              ? wizardData.shift_auto_detect ?? false
              : wizardData.shifts_enabled === false
                ? false
                : null,
          });
        }
      } else if (modalType === "location") {
        if (modalAction === "create") {
          const locationSettings: any = {};
          const locationShiftsEnabled = override.shifts_enabled
            ? wizardData.location_shifts_enabled
            : wizardData.shifts_enabled;
          SETTINGS_KEYS.forEach((s) => {
            if (override[s]) {
              if (s === "timezone") {
                locationSettings[s] = getTimezoneValue(wizardData.location_timezone);
              } else if (s === "shift_auto_detect") {
                if (locationShiftsEnabled) {
                  locationSettings[s] = wizardData.location_shift_auto_detect ?? false;
                }
              } else if (s === "shifts_enabled") {
                locationSettings[s] = wizardData.location_shifts_enabled ?? false;
              } else {
                locationSettings[s] = wizardData[`location_${s}` as keyof WizardData];
              }
            }
          });

          const payload: CompanyLocationCreationData = {
            name: wizardData.location_name!,
            description: wizardData.location_description,
            ...locationSettings,
            company: {
              mode: companyMode,
              company_id: wizardData.company_id!,
              name:
                companyMode === "create" ? wizardData.company_name : undefined,
              status:
                companyMode === "create" ? wizardData.status || 1 : undefined,
              email: companyMode === "create" ? wizardData.email : undefined,
              phone: companyMode === "create" ? wizardData.phone : undefined,
              description: companyMode === "create" ? wizardData.description : undefined,
              ...companySettings,
            },
          };
          await createCompanyLocation(
            wizardData.company_group_id!,
            payload,
          );
        } else {
          const locationPayload: any = {
            name: wizardData.location_name,
            description: wizardData.location_description,
          };
          const locationShiftsEnabled = override.shifts_enabled
            ? wizardData.location_shifts_enabled
            : wizardData.shifts_enabled;
          SETTINGS_KEYS.forEach((s) => {
            if (override[s]) {
              if (s === "timezone") {
                locationPayload[s] = getTimezoneValue(wizardData.location_timezone);
              } else if (s === "shift_auto_detect") {
                if (locationShiftsEnabled) {
                  locationPayload[s] = wizardData.location_shift_auto_detect ?? false;
                } else if (initialOverride[s]) {
                  locationPayload[s] = false;
                }
              } else if (s === "shifts_enabled") {
                locationPayload[s] = wizardData.location_shifts_enabled ?? false;
              } else {
                locationPayload[s] = wizardData[`location_${s}` as keyof WizardData];
              }
            } else if (initialOverride[s]) {
              locationPayload[s] = null;
            }
          });

          await companyLocationAPI.updateLocation(
            wizardData.company_group_id!,
            editingId!,
            locationPayload,
          );
        }
      }
      setModalSuccess(
        modalAction === "create" ? t('successCreated') : t('successUpdated'),
      );
      fetchData(true);
      setTimeout(handleCloseModal, 2000);
    } catch (err: any) {
      setModalError(err.response?.data?.message || t('submitError'));
    } finally {
      setActionLoading(false);
    }
  };



  if (!isSuperAdmin) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-data-page an-settings-page an-page-shell">
          <SettingsPageHeader
            title={t('title')}
            settingsPath={
              currentCompany
                ? `/company/${currentCompany.company_id}/settings`
                : "/settings"
            }
          />
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="text-center">
              <Alert variant="warning">
                <h5>{t('accessDeniedTitle')}</h5>
                <p>{t('accessDeniedBody')}</p>
              </Alert>
            </div>
          </div>
        </Container>
      </Layout>
    );
  }

  if (loading || userRolePermissionsLoading) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-data-page an-settings-page an-page-shell">
          <SettingsPageHeader
            title={t('title')}
            settingsPath={
              currentCompany
                ? `/company/${currentCompany.company_id}/settings`
                : "/settings"
            }
          />
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="text-center">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="text-muted fw-medium">
                {t('loading')}
              </p>
            </div>
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-data-page an-settings-page an-page-shell">
        <SettingsPageHeader
          title={t('title')}
          settingsPath={
            currentCompany
              ? `/company/${currentCompany.company_id}/settings`
              : "/settings"
          }
        />

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <HierarchyTree
          groups={groups}
          locations={locations}
          allCompanies={allCompanies}
          onCreateGroup={() => handleOpenCreateModal("group")}
          onCreateLocation={(groupId) => handleOpenCreateModal("location", groupId)}
          onEditGroup={(group) => handleOpenEditModal("group", group)}
          onEditLocation={(location) => handleOpenEditModal("location", location)}
          onEditCompany={(company) => handleOpenEditCompanyModal(company)}
          onAssignCompany={(location) => handleOpenAssignModal(location)}
          onDeleteGroup={(groupId) => handleDelete("group", groupId)}
          onDeleteLocation={(locationId, groupId) => handleDelete("location", locationId, groupId)}
          onUnassignCompany={(company) => handleUnassignCompany(company)}
        />

        <WizardModal
          show={showModal}
          onHide={handleCloseModal}
          modalType={modalType}
          modalAction={modalAction}
          wizardStep={wizardStep}
          wizardData={wizardData}
          setWizardData={setWizardData}
          companyMode={companyMode}
          setCompanyMode={setCompanyMode}
          override={override}
          setOverride={setOverride}
          companyOverride={companyOverride}
          setCompanyOverride={setCompanyOverride}
          modalError={modalError}
          modalSuccess={modalSuccess}
          actionLoading={actionLoading}
          onNext={handleNext}
          onBack={handleBack}
          onSubmit={handleWizardSubmit}
          availableCompanies={allCompanies.filter(
            (c) => !getCompaniesForLocation(editingId || 0).some(
              (comp) => comp.company_id === c.company_id
            )
          )}
        />

        <Modal
          show={!!confirmDialog?.show}
          onHide={closeConfirmDialog}
          centered
          backdrop="static"
          keyboard={!confirmLoading}
        >
          <Modal.Header closeButton={!confirmLoading}>
            <Modal.Title>{confirmDialog?.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{confirmDialog?.message}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeConfirmDialog} disabled={confirmLoading}>
              {t('cancel', { ns: 'common', keyPrefix: 'buttons' })}
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                if (!confirmDialog?.onConfirm) return;
                await confirmDialog.onConfirm();
              }}
              disabled={confirmLoading}
            >
              {confirmLoading ? <Spinner size="sm" /> : t('confirm')}
            </Button>
          </Modal.Footer>
        </Modal>


      </Container>
    </Layout>
  );
};

export default HierarchyManagement;
